import React, { useState, useEffect, useMemo } from 'react';
import { setFacilityTags, listDirectoryTags } from '../../lib/assessment-kiosk-admin-api';
import { addTag, removeTag, suggestTags, tagOptionLabel, chartCount, chartsByTag } from '../../lib/facility-tags';

// Which Elation chart tags mark a resident of this group home (migration 078,
// O154 F13). A refill sheet from the home is matched only against directory
// charts carrying one of these tags. Each add/remove saves the whole list; the
// server returns it normalized (lower-cased), which is what Elation-tag matching
// compares on.
//
// Scope size is visible (review r1 MINOR-4): every autocomplete option and pill
// carries its chart count, and the block shows the facility's resident_charts
// as "N charts in scope". A number far above the home's census means a broad
// (program) tag was picked, which would widen the home's auto-match.
export function FacilityElationTags({ facility, onSaved }) {
  const [tags, setTags] = useState(Array.isArray(facility.elation_tags) ? facility.elation_tags : []);
  const [draft, setDraft] = useState('');
  const [directoryTags, setDirectoryTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTags(Array.isArray(facility.elation_tags) ? facility.elation_tags : []);
  }, [facility.id, facility.elation_tags]);

  useEffect(() => {
    let live = true;
    listDirectoryTags()
      .then((res) => { if (live) setDirectoryTags(Array.isArray(res?.tags) ? res.tags : []); })
      .catch(() => { /* autocomplete is a convenience; typing still works */ });
    return () => { live = false; };
  }, []);

  const suggestions = useMemo(() => suggestTags(directoryTags, tags, draft), [directoryTags, tags, draft]);
  const counts = useMemo(() => chartsByTag(directoryTags), [directoryTags]);
  // The scope size the server computed for the saved list (resident_charts):
  // a number far above the home's census means a wrong (too broad) tag.
  const inScope = Number.isFinite(Number(facility.resident_charts)) ? Number(facility.resident_charts) : null;
  const listId = `facility-tags-${facility.id}`;

  async function save(next) {
    const prev = tags;
    setTags(next); setSaving(true); setError(null);
    try {
      const row = await setFacilityTags(facility.id, next);
      const saved = Array.isArray(row?.elation_tags) ? row.elation_tags : next;
      setTags(saved);
      if (onSaved) onSaved({ ...facility, ...row, elation_tags: saved });
    } catch (e) { setTags(prev); setError(e.message || 'Could not save tags.'); }
    finally { setSaving(false); }
  }

  async function onAdd() {
    const next = addTag(tags, draft);
    setDraft('');
    if (next.length !== tags.length) await save(next);
  }

  return (
    <div className="kiosk-subsection">
      <h4>Elation resident tags</h4>
      <div className="kiosk-hint">
        Charts carrying any of these Elation tags count as this home's residents when a refill
        sheet from it is matched. Tags are compared ignoring case.
      </div>
      {inScope != null && (
        <div className="kiosk-hint" data-testid="facility-charts-in-scope">
          {chartCount(inScope)} in scope
        </div>
      )}
      {error && <div className="kiosk-admin-error">{error}</div>}

      <div className="onboard-tag-row">
        {tags.length === 0 && <span className="kiosk-hint">No tags yet — no resident is in scope.</span>}
        {tags.map((t) => (
          <span key={t} className="onboard-pill active">
            {t}{counts.has(t) ? ` (${chartCount(counts.get(t))})` : ''}{' '}
            <button className="btn ghost sm" disabled={saving} aria-label={`Remove tag ${t}`}
              onClick={() => save(removeTag(tags, t))}>×</button>
          </span>
        ))}
      </div>

      <div className="kiosk-add-device">
        <input type="text" list={listId} placeholder="Elation tag, e.g. the home's resident tag"
          value={draft} maxLength={120} disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }} />
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s.tag} value={s.tag} label={tagOptionLabel(s)} />)}
        </datalist>
        <button className="btn sm" disabled={saving || !draft.trim()} onClick={onAdd}>
          {saving ? 'Saving…' : 'Add tag'}
        </button>
      </div>
    </div>
  );
}
